import type { AnyRouter } from '@tanstack/react-router';
import type { IObservability } from '../createObservability';
import type { ISpan } from '../tracing/types';

interface INavTiming {
  startedAt: number;
  from: string;
  to: string;
  span: ISpan;
  resolvedAt?: number;
}

/**
 * Instrumenta navegação via router.subscribe.
 * - total: onBeforeNavigate → onResolved (router settled)
 * - render: onBeforeNavigate → onRendered (commit React; view transition pode seguir)
 */
export function instrumentRouter(router: AnyRouter, obs: IObservability): () => void {
  let current: INavTiming | null = null;

  const unsubBefore = router.subscribe('onBeforeNavigate', (evt) => {
    const from = evt.fromLocation?.pathname ?? '(init)';
    const to = evt.toLocation.pathname;
    if (!evt.pathChanged && from === to) return;

    current?.span.end();
    const span = obs.tracing.startSpan('navigation', {
      attributes: { from, to },
    });
    current = { startedAt: performance.now(), from, to, span };
    obs.metrics.increment('navigation.start');
    obs.logger.info('Navigation start', { from, to });
  });

  const unsubResolved = router.subscribe('onResolved', () => {
    if (!current || current.resolvedAt !== undefined) return;
    current.resolvedAt = performance.now();
    const totalMs = current.resolvedAt - current.startedAt;
    obs.metrics.gauge('navigation.duration_ms', totalMs);
    obs.logger.debug('Navigation resolved', {
      from: current.from,
      to: current.to,
      totalMs: Number(totalMs.toFixed(1)),
    });
  });

  const unsubRendered = router.subscribe('onRendered', () => {
    if (!current) return;
    const { startedAt, from, to, span, resolvedAt } = current;
    const renderMs = performance.now() - startedAt;
    const totalMs = (resolvedAt ?? performance.now()) - startedAt;

    span.setAttributes({
      'navigation.total_ms': Number(totalMs.toFixed(1)),
      'navigation.render_ms': Number(renderMs.toFixed(1)),
    });
    span.setStatus({ code: 'ok' });
    span.end();

    obs.metrics.increment('navigation.complete');
    obs.metrics.gauge('navigation.render_ms', renderMs);
    obs.logger.info('Navigation complete', {
      from,
      to,
      totalMs: Number(totalMs.toFixed(1)),
      renderMs: Number(renderMs.toFixed(1)),
    });
    current = null;
  });

  return () => {
    unsubBefore();
    unsubResolved();
    unsubRendered();
    current?.span.end();
    current = null;
  };
}
